/**
 * Regulations API Service
 * Handles regulation-related API requests
 */

import { apiClient } from "./apiClient";
import {
  formatAutoTagsForListDisplay,
  parseRegulationAutoTags,
  resolveDisplayTagGroups,
  type RegulationAutoTagGroup,
} from "./regulationAutoTags";
import { parseWorkflowTaskDetailFromApiResponse } from "../utils/workflowTaskDetailNormalize";

/**
 * Concept value item (for dropdown options)
 */
export interface ConceptValue {
  id: string;
  code: string;
  name: string;
  [key: string]: any; // Allow other fields
}

/**
 * Field configuration from regulations/fields API
 */
export interface FieldConfig {
  field_name: string;
  display_name: string;
  field_type: "string" | "array" | "date";
  pages: string[];
  sortable: boolean;
  filterable: boolean;
  filter_operators: string[] | null;
  input_type: "text" | "dropdown" | "date_range" | null;
  default_display: boolean;
  description: string;
  /**
   * When this references auto_tags (e.g. "auto_tags", "auto_tags.Country"),
   * the column is a dynamic auto-tag concept field (Country / Department / …).
   */
  filter_field_name?: string | null;
}

function matchesAutoTagsFieldRef(value: unknown): boolean {
  if (typeof value === "string") {
    const normalized = value.toLowerCase().replace(/[\s_-]/g, "");
    return normalized.includes("autotags");
  }
  if (Array.isArray(value)) {
    return value.some((item) => matchesAutoTagsFieldRef(item));
  }
  return false;
}

/**
 * Dynamic auto-tag concept columns from fields API
 * (`filter_field_name` / aliases reference auto_tags).
 */
export function isDynamicAutoTagsField(
  field: Pick<FieldConfig, "filter_field_name" | "field_name"> & {
    filterFieldName?: unknown;
  },
): boolean {
  if (
    matchesAutoTagsFieldRef(field.filter_field_name) ||
    matchesAutoTagsFieldRef(field.filterFieldName)
  ) {
    return true;
  }
  // Nested API names such as "auto_tags.Country" (excluding the fixed auto_tags column).
  if (
    typeof field.field_name === "string" &&
    field.field_name.toLowerCase() !== "auto_tags" &&
    matchesAutoTagsFieldRef(field.field_name)
  ) {
    return true;
  }
  return false;
}

/** Normalize fields API payloads (snake_case / camelCase) into FieldConfig. */
export function normalizeRegulationsFieldConfig(
  raw: Record<string, unknown>,
): FieldConfig {
  const pagesRaw = raw.pages;
  const pages = Array.isArray(pagesRaw)
    ? pagesRaw.map((p) => String(p))
    : [];
  const filterOperatorsRaw = raw.filter_operators ?? raw.filterOperators;
  const filter_operators = Array.isArray(filterOperatorsRaw)
    ? filterOperatorsRaw.map((op) => String(op))
    : null;
  const filterField =
    raw.filter_field_name ?? raw.filterFieldName ?? null;

  return {
    field_name: String(raw.field_name ?? raw.fieldName ?? ""),
    display_name: String(raw.display_name ?? raw.displayName ?? ""),
    field_type: (raw.field_type ?? raw.fieldType ?? "string") as FieldConfig["field_type"],
    pages,
    sortable: Boolean(raw.sortable),
    filterable: Boolean(raw.filterable),
    filter_operators,
    input_type: (raw.input_type ??
      raw.inputType ??
      null) as FieldConfig["input_type"],
    default_display: Boolean(raw.default_display ?? raw.defaultDisplay),
    description: String(raw.description ?? ""),
    filter_field_name:
      filterField == null
        ? null
        : Array.isArray(filterField)
          ? filterField.map(String).join(",")
          : String(filterField),
  };
}

/**
 * Regulations fields response
 */
export interface RegulationsFieldsData {
  fields: FieldConfig[];
  page_type: string;
}

/**
 * Concept values query parameters
 */
export interface ConceptValuesRequest {
  concept_name: string; // e.g., 'country'
  name?: string; // Search keyword
}

/**
 * Search filter conditions
 */
export interface SearchFilter {
  /** Status */
  status?: string;
  /** Condition array */
  conditions?: Array<{
    field: string;
    /** Same tokens as GET /regulations/fields `filter_operators` (e.g. starts_with, eq, like). */
    operator: string;
    value: string | number | boolean | string[];
  }>;
}

/**
 * Pagination parameters
 */
export interface PaginationParams {
  /** Page number */
  page: number;
  /** Page size */
  page_size: number;
}

/** Coerce POST /regulations/search pagination so JSON never sends null/NaN (backend expects integers). */
function normalizeSearchPaginationParams(
  p?: PaginationParams | null,
): PaginationParams {
  const DEFAULT_PAGE = 1;
  const DEFAULT_SIZE = 20;
  if (p == null) {
    return { page: DEFAULT_PAGE, page_size: DEFAULT_SIZE };
  }
  const pageNum = Number(p.page);
  const sizeNum = Number(p.page_size);
  const page =
    Number.isFinite(pageNum) && pageNum >= 1
      ? Math.trunc(pageNum)
      : DEFAULT_PAGE;
  const page_size =
    Number.isFinite(sizeNum) && sizeNum >= 1
      ? Math.trunc(sizeNum)
      : DEFAULT_SIZE;
  return { page, page_size };
}

/** Single sort clause for POST /regulations/search */
export interface RegulationsSortItem {
  field: string;
  order: "asc" | "desc";
}

/**
 * Search request parameters
 */
export interface RegulationsSearchRequest {
  /** Search keyword */
  query?: string;
  /** Filter conditions */
  filters?: SearchFilter;
  /** Pagination parameters */
  pagination?: PaginationParams;
  /** Fields to return */
  fields?: string[];
  /** Server-side sort (Data Grid column fields mapped to API field names) */
  sort?: RegulationsSortItem[];
}

/** Maps Data Grid column `field` values to API sort field names (snake_case). */
const GRID_SORT_FIELD_TO_API: Record<string, string> = {
  id: "regulation_id",
  title: "title",
  version: "version",
  markets: "markets",
  productTypes: "product_types",
  publishedDate: "published_date",
  releaseDate: "release_date",
  effectiveDate: "effective_date",
  synopsis: "synopsis_withouthtml",
  modifiedDate: "modified_date",
  relatedItems: "related_regulations",
  receivedDate: "created_date",
  status: "regulations_status",
  impactedConcepts: "impacted_concepts",
  tags: "tags",
  autoTags: "auto_tags",
};

export function gridSortFieldToApiField(gridField: string): string {
  return GRID_SORT_FIELD_TO_API[gridField] ?? gridField;
}

/** Optional overrides: Data Grid column field → search `conditions[].field`. */
export type GridFilterApiFieldMap = Readonly<Record<string, string>>;

/**
 * Resolve the API filter field for a grid column.
 * Prefer fields-API `filter_field_name` overrides (dynamic auto_tags columns).
 */
export function gridFilterFieldToApiField(
  gridField: string,
  fieldMap?: GridFilterApiFieldMap | null,
): string {
  const override = fieldMap?.[gridField];
  if (typeof override === "string" && override.trim()) {
    return override.trim();
  }
  return gridSortFieldToApiField(gridField);
}

/** Build `sort` payload from MUI Data Grid sort model items */
export function regulationsSortFromGridSortModel(
  items: ReadonlyArray<{ field?: string; sort?: "asc" | "desc" | null }>,
): RegulationsSortItem[] | undefined {
  if (!items?.length) return undefined;
  const sort: RegulationsSortItem[] = [];
  for (const item of items) {
    if (item.sort !== "asc" && item.sort !== "desc") continue;
    if (item.field == null || item.field === "") continue;
    sort.push({
      field: gridSortFieldToApiField(String(item.field)),
      order: item.sort,
    });
  }
  return sort.length > 0 ? sort : undefined;
}

/** Single row in `filters.conditions` for POST /regulations/search */
export type RegulationsSearchCondition = NonNullable<
  SearchFilter["conditions"]
>[number];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Normalize filter values from MUI Data Grid (Date, ISO string, number, etc.) for the search API */
export function serializeGridFilterValueForApi(
  value: unknown,
): string | number {
  if (value == null) return "";
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "boolean") return value ? "true" : "false";
  if (value instanceof Date) {
    return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(
      value.getDate(),
    )}`;
  }
  const s = String(value).trim();
  const isoDay = /^(\d{4}-\d{2}-\d{2})/.exec(s);
  if (isoDay) return isoDay[1];
  return s;
}

/**
 * Map one Data Grid filter row to a search `conditions` entry.
 * Skips non-persistable ops (e.g. doesNotContain) and UI-only columns (`actions`).
 */
export function regulationsConditionFromGridFilterItem(
  item: {
    field?: string;
    operator?: string;
    value?: unknown;
  },
  fieldMap?: GridFilterApiFieldMap | null,
): RegulationsSearchCondition | null {
  const rawField = item.field;
  if (rawField == null || rawField === "" || rawField === "actions") {
    return null;
  }

  const field = gridFilterFieldToApiField(String(rawField), fieldMap);
  const op = item.operator ?? "";
  const v = item.value;

  const noValueOps = new Set([
    "isEmpty",
    "isNotEmpty",
    "isempty",
    "is_empty",
    "isnotempty",
    "is_not_empty",
  ]);
  if (!noValueOps.has(op)) {
    if (v === undefined) return null;
    if (
      v !== null &&
      typeof v === "string" &&
      v.trim() === "" &&
      !["isEmpty"].includes(op)
    ) {
      return null;
    }
    if (op !== "isAnyOf" && op !== "in" && v === null) return null;
  }

  const scalar = (): string | number => serializeGridFilterValueForApi(v);

  /** MUI default operator values when the grid row still uses built-in operator names. */
  switch (op) {
    case "contains": {
      const s = String(v ?? "").trim();
      if (!s) return null;
      return { field, operator: "contains", value: s };
    }
    case "doesNotContain":
      return null;
    case "equals":
    case "=":
    case "is":
      return { field, operator: "eq", value: scalar() };
    case "doesNotEqual":
    case "!=":
    case "not":
      return { field, operator: "ne", value: scalar() };
    case "startsWith": {
      const s = String(v ?? "").trim();
      if (!s) return null;
      return { field, operator: "startsWith", value: s };
    }
    case "endsWith": {
      const s = String(v ?? "").trim();
      if (!s) return null;
      return { field, operator: "endsWith", value: s };
    }
    case "isEmpty":
      return { field, operator: "eq", value: "" };
    case "isNotEmpty":
      return { field, operator: "ne", value: "" };
    case "isAnyOf": {
      if (!Array.isArray(v) || v.length === 0) return null;
      return {
        field,
        operator: "in",
        value: v.map((x) => String(serializeGridFilterValueForApi(x))),
      };
    }
    case ">":
      return { field, operator: "gt", value: scalar() };
    case ">=":
    case "onOrAfter":
      return { field, operator: "gte", value: scalar() };
    case "<":
      return { field, operator: "lt", value: scalar() };
    case "<=":
    case "onOrBefore":
      return { field, operator: "lte", value: scalar() };
    case "after":
      return { field, operator: "gt", value: scalar() };
    case "before":
      return { field, operator: "lt", value: scalar() };
    default:
      break;
  }

  // Backend `filter_operators` tokens stored on the filter row — pass operator through as-is.
  if (op === "isAnyOf" || op === "in") {
    if (!Array.isArray(v) || v.length === 0) return null;
    return {
      field,
      operator: op,
      value: v.map((x) => String(serializeGridFilterValueForApi(x))),
    };
  }

  if (noValueOps.has(op)) {
    return { field, operator: op, value: "" };
  }

  const trimmed = String(v ?? "").trim();
  if (trimmed === "") return null;

  return { field, operator: op, value: scalar() };
}

/** Stable key for server search deps — only changes when API conditions change. */
export function gridFilterConditionsCacheKey(
  model:
    | {
        items?: ReadonlyArray<{
          field?: string;
          operator?: string;
          value?: unknown;
        }>;
      }
    | null
    | undefined,
  fieldMap?: GridFilterApiFieldMap | null,
): string {
  return JSON.stringify(
    regulationsConditionsFromGridFilterModel(model, fieldMap),
  );
}

/** Build `filters.conditions` entries from the Data Grid filter panel model */
export function regulationsConditionsFromGridFilterModel(
  model:
    | {
        items?: ReadonlyArray<{
          field?: string;
          operator?: string;
          value?: unknown;
        }>;
      }
    | null
    | undefined,
  fieldMap?: GridFilterApiFieldMap | null,
): RegulationsSearchCondition[] {
  if (!model?.items?.length) return [];
  const out: RegulationsSearchCondition[] = [];
  for (const item of model.items) {
    const row = regulationsConditionFromGridFilterItem(item, fieldMap);
    if (row) out.push(row);
  }
  return out;
}

/**
 * Market data structure
 */
export interface Market {
  code: string;
  name: string;
  id: string;
  children?: Array<{
    code: string;
    name: string;
    id: string;
    modified_date?: string;
  }>;
}

/**
 * Product type data structure
 */
export interface ProductType {
  is_deleted: boolean;
  code: string;
  name: string;
  id: string;
}

/**
 * Category data structure (same level as product_types in search response)
 */
export interface CategoryItem {
  code: string;
  name: string;
  id: string;
  children?: CategoryItem[];
}

/** Item in regulation list `impacted_concepts` (AI); display name often comes from `concept_value.name` after normalize. */
export interface ImpactedConceptItem {
  name?: string;
  code?: string;
}

/**
 * Regulation data item (raw format from backend)
 */
export interface RegulationItem {
  id: string;
  regulation_id: string;
  title: string;
  version: string;
  status: number;
  regulations_status: string;
  release_date: string;
  published_date: string;
  effective_date?: string;
  markets: Market[];
  product_types: ProductType[];
  categories?: CategoryItem[];
  /** Plain-text synopsis for list table (no HTML) */
  synopsis_withouthtml?: string;
  synopsis?: string;
  modified_date?: string;
  related_items?: string;
  /** List/fields API may use this name for Related Items column */
  related_regulations?: string | unknown;
  received_date?: string;
  /** Fields API / search may use this name for Receive Date */
  created_date?: string;
  /** User has marked this regulation as favorite (list/search response) */
  is_favorite?: boolean;
  /** AI impacted concepts for list column — array of rows, plain string (e.g. `"No impact"`), or a single row object. */
  impacted_concepts?: ImpactedConceptItem[] | string | ImpactedConceptItem;
  /** Same field as camelCase (some APIs / serializers) */
  impactedConcepts?: ImpactedConceptItem[] | string | ImpactedConceptItem;
  /** Auto-tagging results grouped by concept name. */
  auto_tags?: Record<
    string,
    Array<{ name?: string; code?: string; description?: string }>
  >;
  /**
   * Backend-merged tags for Tags Details / Tags column (auto + manual).
   * Same shapes as `auto_tags` or a category tree.
   */
  merged_tags?:
    | Record<
        string,
        Array<{ name?: string; code?: string; description?: string }>
      >
    | RegulationAutoTagGroup[]
    | CategoryItem[];
  /** List column "Reference ID" — search/list payload field. */
  reference_insight_id?: string | number | null;
}

/**
 * Raw item from POST /regulations/search (`tags` replaces legacy `categories`).
 */
export interface RegulationSearchApiItem
  extends Omit<RegulationItem, "categories"> {
  tags?: CategoryItem[];
}

/**
 * Search response data (format from backend)
 */
export interface RegulationsSearchData {
  total: number;
  page: number;
  page_size: number;
  items: RegulationItem[];
}

/** Map POST /regulations/search items to RegulationItem for shared transforms. */
export function normalizeRegulationSearchApiItem(
  item: RegulationSearchApiItem,
): RegulationItem {
  const { tags, ...rest } = item;
  return {
    ...rest,
    categories: tags,
  };
}

/** Whether a fields-config / detail field name is the category tree (API alias: tags). */
export function isRegulationCategoriesFieldName(fieldName: string): boolean {
  return fieldName === "categories" || fieldName === "tags";
}

export function pickRegulationCategoriesField(
  getField: (name: string) => FieldConfig | undefined,
): FieldConfig | undefined {
  return getField("categories") ?? getField("tags");
}

export function pickRegulationCategoriesValue(
  data: Record<string, unknown> | null | undefined,
): unknown {
  if (!data) return undefined;
  return data.categories ?? data.tags;
}

/**
 * API response format
 */
export interface ApiResponse<T = any> {
  status: "success" | "error";
  code: number;
  message?: string;
  data: T;
}

/**
 * Table row data (for display)
 */
export interface RegulationRow {
  id: string;
  regulation_id: string;
  title: string;
  version: string;
  markets: string;
  productTypes: string;
  publishedDate: string;
  releaseDate: string;
  effectiveDate?: string;
  tags?: string;
  status: string;
  synopsis?: string;
  modifiedDate?: string;
  relatedItems?: string;
  receivedDate?: string;
  isNew?: boolean;
  isFavorite?: boolean;
  impactedConcepts?: ImpactedConceptItem[];
  /**
   * Display tag groups for the Tags column / Tags Details modal:
   * prefer `merged_tags`, else AC7 (auto covers manual when present).
   */
  tagGroups?: RegulationAutoTagGroup[];
  /**
   * Raw categories/tags tree. Included in the home tag count and shown
   * under merged groups in Tags Details.
   */
  categoryItems?: unknown[];
  /** Comma-joined auto tag names for list display */
  autoTags?: string;
  /** Structured auto tags for list View All dialog */
  autoTagGroups?: RegulationAutoTagGroup[];
  /**
   * Whether the API payload included `auto_tags`.
   * Empty TAG UI: absent → "NA"; present empty → "-".
   */
  autoTagsFieldPresent?: boolean;
  /** List column "Reference ID" — from API `reference_insight_id`. */
  referenceInsightId?: string;
  /**
   * Extra list columns from fields/search API (grid field key → display string).
   * Known columns above are mapped explicitly and are not overwritten here.
   */
  [extraField: string]: unknown;
}

/**
 * Whether to use mock data
 * Development environment uses mock by default, production uses real API
 * Set to false temporarily to test real API in development
 */

export type ListMultiValueCellSortOrder = "asc" | "desc";

/** Active grid sort direction for a multi-value list column, if any. */
export function getListCellSortOrderForField(
  sortModel: ReadonlyArray<{ field?: string; sort?: "asc" | "desc" | null }>,
  gridField: string,
): ListMultiValueCellSortOrder | null {
  for (const item of sortModel) {
    if (
      item.field === gridField &&
      (item.sort === "asc" || item.sort === "desc")
    ) {
      return item.sort;
    }
  }
  return null;
}

/**
 * Names in a comma-separated list cell (Tags, markets, product types).
 * Same split the table uses before it re-orders a sorted column.
 */
export function splitCommaSeparatedDisplayValues(
  value: string | undefined,
): string[] {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed) return [];
  return trimmed
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Re-order comma-separated cell text when the column is sorted (first letter, asc/desc).
 * When `sortOrder` is null, preserves the stored string order from the API.
 */
export function formatCommaSeparatedListForDisplay(
  value: string | undefined,
  sortOrder: ListMultiValueCellSortOrder | null,
): string {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed || sortOrder == null) return trimmed;

  const parts = splitCommaSeparatedDisplayValues(trimmed);
  parts.sort((a, b) => {
    const cmp = a.localeCompare(b, undefined, { sensitivity: "base" });
    return sortOrder === "desc" ? -cmp : cmp;
  });
  return parts.join(", ");
}

/**
 * Convert market data to string
 */
function formatMarkets(markets: Market[]): string {
  if (!markets || markets.length === 0) return "";

  const marketNames: string[] = [];
  markets.forEach((market) => {
    if (market.children && market.children.length > 0) {
      // Show child market names
      market.children.forEach((child) => {
        marketNames.push(child.name);
      });
    } else {
      // Show parent market name
      marketNames.push(market.name);
    }
  });

  return marketNames.join(", ");
}

/** Soft-deleted product types must not appear in list or detail UI. */
export function visibleProductTypes<T>(
  productTypes: T[] | null | undefined,
): T[] {
  if (!productTypes?.length) return [];
  return productTypes.filter((pt) => {
    if (pt == null) return false;
    if (typeof pt !== "object") return true;
    return (pt as { is_deleted?: boolean }).is_deleted !== true;
  });
}

/**
 * Convert product type data to string
 */
function formatProductTypes(productTypes: ProductType[]): string {
  const visible = visibleProductTypes(productTypes);
  if (visible.length === 0) return "";
  return visible.map((pt) => pt.name).join(", ");
}

/**
 * Recursively collect all category names from tree (parent + children)
 */
export function collectCategoryNames(
  cats: CategoryItem[] | undefined,
): string[] {
  const names: string[] = [];
  if (!cats) return names;
  for (const c of cats) {
    names.push(c.name);
    if (c.children?.length) {
      names.push(...collectCategoryNames(c.children));
    }
  }
  return names;
}

/**
 * Convert categories tree to comma-separated names for Tags column display
 */
export function formatCategories(categories?: CategoryItem[]): string {
  if (!categories || categories.length === 0) return "";
  return collectCategoryNames(categories).join(", ");
}

/**
 * Format date string
 */
function formatDate(dateString: string): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  // Keep UTC semantics consistent with previous toISOString().split("T")[0] logic.
  // Output required format: DD-MMM-YYYY (e.g., 11-Mar-2026).
  const [yyyy, mm, dd] = date
    .toISOString()
    .split("T")[0]
    .split("-");
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthIndex = Number(mm) - 1;
  if (monthIndex < 0 || monthIndex > 11) return "";
  return `${dd}-${monthNames[monthIndex]}-${yyyy}`;
}

/** API keys already handled by the explicit RegulationRow mapping. */
const KNOWN_LIST_API_FIELD_NAMES = new Set([
  "id",
  "regulation_id",
  "title",
  "version",
  "status",
  "regulations_status",
  "markets",
  "product_types",
  "categories",
  "tags",
  "auto_tags",
  "published_date",
  "release_date",
  "effective_date",
  "modified_date",
  "synopsis",
  "synopsis_withouthtml",
  "related_items",
  "related_regulations",
  "created_date",
  "received_date",
  "is_favorite",
  "isFavorite",
  "impacted_concepts",
  "impactedConcepts",
  "reference_insight_id",
]);

/** Grid row keys that must not be overwritten by dynamic extras. */
const KNOWN_LIST_ROW_KEYS = new Set([
  "id",
  "regulation_id",
  "title",
  "version",
  "markets",
  "productTypes",
  "publishedDate",
  "releaseDate",
  "effectiveDate",
  "tags",
  "tagGroups",
  "categoryItems",
  "status",
  "synopsis",
  "modifiedDate",
  "relatedItems",
  "receivedDate",
  "isNew",
  "isFavorite",
  "impactedConcepts",
  "autoTags",
  "autoTagGroups",
  "autoTagsFieldPresent",
  "referenceInsightId",
]);

function apiFieldNameToRowKey(fieldName: string): string {
  return fieldName.replace(/_([a-z])/g, (_, letter: string) =>
    letter.toUpperCase(),
  );
}

/** Format unknown list-cell values for PlainTextEllipsisCell display. */
export function formatDynamicListCellValue(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      return formatDate(trimmed) || trimmed;
    }
    return trimmed;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => formatDynamicListCellValue(item))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const label = extractConceptDisplayName(record);
    if (label) return label;
    const nested =
      record.value ?? record.values ?? record.items ?? record.data;
    if (nested != null && nested !== value) {
      return formatDynamicListCellValue(nested);
    }
  }
  return "";
}

function appendDynamicListFields(
  row: RegulationRow,
  itemRecord: Record<string, unknown>,
): RegulationRow {
  const next: RegulationRow = { ...row };
  for (const [apiKey, rawValue] of Object.entries(itemRecord)) {
    if (KNOWN_LIST_API_FIELD_NAMES.has(apiKey)) continue;
    const rowKey = apiFieldNameToRowKey(apiKey);
    if (!rowKey || KNOWN_LIST_ROW_KEYS.has(rowKey)) continue;
    if (
      Object.prototype.hasOwnProperty.call(next, rowKey) &&
      next[rowKey] != null &&
      next[rowKey] !== ""
    ) {
      continue;
    }
    const formatted = formatDynamicListCellValue(rawValue);
    if (formatted === "" && rawValue != null && rawValue !== "") {
      // Keep a stable empty display for present-but-empty structured values.
      next[rowKey] = "";
      continue;
    }
    next[rowKey] = formatted;
  }

  // Project nested auto_tags concept buckets onto dynamic columns when missing.
  const autoTagsRaw = itemRecord.auto_tags;
  if (autoTagsRaw && typeof autoTagsRaw === "object" && !Array.isArray(autoTagsRaw)) {
    for (const [concept, valuesRaw] of Object.entries(
      autoTagsRaw as Record<string, unknown>,
    )) {
      const rowKey = apiFieldNameToRowKey(concept);
      if (!rowKey || KNOWN_LIST_ROW_KEYS.has(rowKey)) continue;
      if (
        Object.prototype.hasOwnProperty.call(next, rowKey) &&
        next[rowKey] != null &&
        next[rowKey] !== ""
      ) {
        continue;
      }
      next[rowKey] = formatDynamicListCellValue(valuesRaw);
    }
  }

  return next;
}

/** Resolve human-readable label from API values (strings or nested concept objects). */
function extractConceptDisplayName(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value).trim();
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    const o = value as Record<string, unknown>;
    const inner =
      o.name ??
      o.concept_name ??
      o.display_name ??
      o.label ??
      o.title ??
      o.concept;
    if (inner !== undefined && inner !== value) {
      const nested = extractConceptDisplayName(inner);
      if (nested) return nested;
    }
  }
  return "";
}

function resolveImpactedConceptRow(
  o: Record<string, unknown>,
): {
  name: string;
  code?: string;
} | null {
  const cv = o.concept_value;
  const cvObj =
    cv && typeof cv === "object" && !Array.isArray(cv)
      ? (cv as Record<string, unknown>)
      : null;

  const name =
    extractConceptDisplayName(cv) ||
    extractConceptDisplayName(o.name) ||
    extractConceptDisplayName(o.concept_name) ||
    extractConceptDisplayName(o.display_name) ||
    extractConceptDisplayName(o.title) ||
    extractConceptDisplayName(o.label) ||
    extractConceptDisplayName(o.concept);

  const codeRaw =
    o.code ??
    o.concept_code ??
    (cvObj?.code != null && typeof cvObj.code !== "object"
      ? cvObj.code
      : undefined);
  let code: string | undefined;
  if (codeRaw != null && typeof codeRaw !== "object") {
    const cs = String(codeRaw).trim();
    if (cs) code = cs;
  } else if (
    o.concept &&
    typeof o.concept === "object" &&
    !Array.isArray(o.concept)
  ) {
    const cc = (o.concept as Record<string, unknown>).code;
    if (cc != null && typeof cc !== "object") {
      const cs = String(cc).trim();
      if (cs) code = cs;
    }
  }

  if (!name) return null;
  return code ? { name, code } : { name };
}

/**
 * Normalize impacted concepts from list/search API (handles snake/camel key and alternate name fields).
 * Some APIs return a plain string (e.g. `"No impact"`) instead of an array — treat as a single display row.
 */
export function normalizeImpactedConceptsFromApi(
  item: RegulationItem & Record<string, unknown>,
): ImpactedConceptItem[] {
  const raw =
    item.impacted_concepts ??
    item.impactedConcepts ??
    (item as Record<string, unknown>).impacted_items ??
    (item as Record<string, unknown>).impactedItems;

  if (raw == null) return [];

  if (typeof raw === "string") {
    const name = raw.trim();
    return name ? [{ name }] : [];
  }

  if (!Array.isArray(raw)) {
    if (raw && typeof raw === "object") {
      const resolved = resolveImpactedConceptRow(
        raw as Record<string, unknown>,
      );
      return resolved ? [resolved] : [];
    }
    return [];
  }

  const rows: ImpactedConceptItem[] = [];
  for (const entry of raw) {
    if (typeof entry === "string") {
      const name = entry.trim();
      if (name) rows.push({ name });
      continue;
    }
    if (entry && typeof entry === "object") {
      const resolved = resolveImpactedConceptRow(
        entry as Record<string, unknown>,
      );
      if (resolved) {
        rows.push(resolved);
      }
    }
  }
  return rows;
}

function formatRelatedItemsListValue(
  relatedItems?: string,
  relatedRegulations?: unknown,
): string {
  // Prefer structured related_regulations so deleted items can be excluded
  // (same rule as RelatedItemsSection on the detail page).
  if (Array.isArray(relatedRegulations)) {
    return relatedRegulations
      .filter((entry) => {
        if (entry == null) return false;
        if (typeof entry === "object") {
          return (entry as { is_deleted?: boolean }).is_deleted !== true;
        }
        return true;
      })
      .map((entry) => {
        if (typeof entry === "string") return entry;
        if (typeof entry === "object") {
          const o = entry as Record<string, unknown>;
          return String(
            o.regulation_id ?? o.id ?? o.title ?? o.name ?? "",
          ).trim();
        }
        return String(entry);
      })
      .filter(Boolean)
      .join(", ");
  }
  if (relatedItems != null && String(relatedItems).trim() !== "") {
    return String(relatedItems);
  }
  if (relatedRegulations == null) return "";
  if (typeof relatedRegulations === "string") return relatedRegulations;
  return String(relatedRegulations);
}

/** Linear-time HTML tag strip (avoids ReDoS from tag-matching regexes). */
function stripHtmlTagsToSpaces(html: string): string {
  let out = "";
  let inTag = false;
  for (let i = 0; i < html.length; i++) {
    const char = html[i];
    if (char === "<") {
      inTag = true;
      out += " ";
      continue;
    }
    if (char === ">") {
      inTag = false;
      continue;
    }
    if (!inTag) {
      out += char;
    }
  }
  return out;
}

function collapseWhitespace(text: string): string {
  let out = "";
  let prevSpace = false;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    const isSpace =
      code === 0x20 ||
      code === 0x09 ||
      code === 0x0a ||
      code === 0x0d ||
      code === 0x0c ||
      code === 0x0b;
    if (isSpace) {
      if (out.length > 0 && !prevSpace) {
        out += " ";
        prevSpace = true;
      }
    } else {
      out += text[i];
      prevSpace = false;
    }
  }
  return out.trim();
}

function formatReferenceInsightId(
  value: string | number | null | undefined,
): string {
  if (value == null) return "";
  return String(value).trim();
}

/** Plain synopsis for list display; prefers `synopsis_withouthtml`, strips HTML from legacy `synopsis`. */
export function synopsisPlainTextForList(
  item: Pick<RegulationItem, "synopsis_withouthtml" | "synopsis">,
): string {
  const plain = item.synopsis_withouthtml;
  if (plain != null && String(plain).trim() !== "") {
    return String(plain).trim();
  }
  const legacy = item.synopsis;
  if (legacy == null || String(legacy).trim() === "") return "";
  return collapseWhitespace(stripHtmlTagsToSpaces(String(legacy)));
}

/**
 * Transform backend data to table row data
 */
export function transformToTableRow(item: RegulationItem): RegulationRow {
  // Calculate if regulation is new (published within last 7 days)
  const isNew = (() => {
    if (!item.published_date) return false;
    const publishedDate = new Date(item.published_date);
    const today = new Date();
    const diffTime = today.getTime() - publishedDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays >= 0;
  })();

  const itemRecord = item as RegulationItem & Record<string, unknown>;
  const autoTagGroups = parseRegulationAutoTags(itemRecord);
  const autoTagsFieldPresent = Object.hasOwn(itemRecord, "auto_tags");
  const tagGroups = resolveDisplayTagGroups(itemRecord);
  const rawCategories = pickRegulationCategoriesValue(itemRecord);
  const categoryItems = Array.isArray(rawCategories) ? rawCategories : [];

  const row: RegulationRow = {
    id: item.regulation_id || item.id,
    regulation_id: item.regulation_id,
    title: item.title,
    version: item.version,
    markets: formatMarkets(item.markets),
    productTypes: formatProductTypes(item.product_types),
    publishedDate: formatDate(item.published_date),
    releaseDate: formatDate(item.release_date),
    effectiveDate: formatDate(item.effective_date ?? ""),
    tags: formatCategories(item.categories),
    tagGroups,
    ...(categoryItems.length > 0 ? { categoryItems } : {}),
    status: item.regulations_status || "Unknown",
    synopsis: synopsisPlainTextForList(item),
    modifiedDate: formatDate(item.modified_date || ""),
    relatedItems: formatRelatedItemsListValue(
      item.related_items,
      item.related_regulations,
    ),
    receivedDate: formatDate(item.created_date || item.received_date || ""),
    isNew,
    isFavorite: Boolean(item.is_favorite),
    impactedConcepts: normalizeImpactedConceptsFromApi(itemRecord),
    autoTags: formatAutoTagsForListDisplay(autoTagGroups),
    autoTagGroups: autoTagGroups ?? undefined,
    autoTagsFieldPresent,
    referenceInsightId: formatReferenceInsightId(item.reference_insight_id),
  };

  return appendDynamicListFields(row, itemRecord);
}

/**
 * Search regulations
 */
export async function searchRegulations(
  params: RegulationsSearchRequest = {},
): Promise<ApiResponse<RegulationsSearchData>> {
  // Build request with defaults
  const requestBody: RegulationsSearchRequest = {
    query: params.query || "",
    filters: params.filters || {
      status: "3",
      conditions: [],
    },
    pagination: normalizeSearchPaginationParams(params.pagination),
    fields: params.fields || [],
  };
  if (params.sort != null && params.sort.length > 0) {
    requestBody.sort = params.sort;
  }

  try {
    // Debug logs (also asserted in unit tests)
    console.log(
      `API Request to: /regulations/search ${JSON.stringify(requestBody)}`,
    );
    const result = await apiClient.post<{
      total: number;
      page: number;
      page_size: number;
      items: RegulationSearchApiItem[];
    }>("/regulations/search", requestBody);

    // console.log("✅ API Response:", JSON.stringify(result));

    const raw = result.data!;
    const data: RegulationsSearchData = {
      total: raw.total,
      page: raw.page,
      page_size: raw.page_size,
      items: raw.items.map(normalizeRegulationSearchApiItem),
    };

    // Backend response format: { status, code, data: { total, page, page_size, items } }
    return {
      status: (result.status as "success" | "error") || "success",
      code: result.code,
      message: result.message || "Success",
      data,
    };
  } catch (error) {
    console.error("❌ Failed to search regulations:", error);
    throw error;
  }
}

/** Query params for GET /regulations/favorites (pagination only) */
export interface RegulationsFavoritesRequest {
  page: number;
  page_size: number;
}

/**
 * User's favorited regulations list (GET /regulations/favorites?page=&page_size=)
 */
export async function fetchRegulationFavorites(
  params: RegulationsFavoritesRequest,
): Promise<ApiResponse<RegulationsSearchData>> {
  try {
    const query: RegulationsFavoritesRequest = {
      page: params.page,
      page_size: params.page_size,
    };
    const result = await apiClient.get<RegulationsSearchData>(
      "/regulations/favorites",
      query,
    );
    return {
      status: (result.status as "success" | "error") || "success",
      code: result.code,
      message: result.message || "Success",
      data: result.data!,
    };
  } catch (error) {
    console.error("❌ Failed to fetch regulation favorites:", error);
    throw error;
  }
}

/**
 * Global search record structure
 */
export interface GlobalSearchRecord {
  source: {
    id: string;
    insight_id: string;
    title: string;
    version: string;
    releaseDate: string;
    effectiveDate: string;
    comingIntoForceDate?: string;
    synopsis: string;
    description?: string;
    keywords?: string;
    markets: Market[];
    productType: ProductType[];
    categories: CategoryItem[];
    associatedDocuments: Array<{ name: string; filepath: string }>;
    referenceLinks: Array<{ name: string }>;
    domain_name?: string;
    [key: string]: any;
  };
  highlight?: {
    synopsis?: string[];
    title?: string[];
    /** Backend may use nested path keys (e.g. Elasticsearch highlight field names). */
    "markets.children.name"?: string[];
    "categories.children.children.name"?: string[];
    "domain_id.name"?: string[];
    domain_name?: string[];
    markets?: string[];
    productType?: string[];
    product_types?: string[];
    categories?: string[];
    [key: string]: string[] | undefined;
  };
  score: number;
}

/**
 * Global search response data
 */
export interface GlobalSearchData {
  total: number;
  page: number;
  size: number;
  records: GlobalSearchRecord[];
}

/**
 * Global search API parameters
 */
export interface GlobalSearchRequest {
  page: number;
  pagesize: number;
  query: string;
  semantic_search?: boolean;
  /** Ignored by client: stripped before POST /global-search until backend supports it. */
  conditions?: Array<Record<string, any>>;
  includes?: string[];
  excludes?: string[];
}

/**
 * Global search
 */
export async function globalSearch(
  params: GlobalSearchRequest,
): Promise<ApiResponse<GlobalSearchData>> {
  try {
    // Do not send `conditions` in the request body for now (backend / product).
    const { conditions: _omitConditions, ...body } = params;
    void _omitConditions;
    const result = await apiClient.post<GlobalSearchData>(
      "/global-search",
      body,
    );

    // Check if the result is in the standard wrapper format or flat
    if (
      result &&
      typeof result === "object" &&
      "status" in result &&
      "data" in result
    ) {
      return {
        status: (result.status as "success" | "error") || "success",
        code: result.code as number,
        message: result.message || "Success",
        data: result.data as GlobalSearchData,
      };
    }

    // Flat format: { total, page, size, records }
    return {
      status: "success",
      code: 200,
      message: "Success",
      data: (result as unknown) as GlobalSearchData,
    };
  } catch (error) {
    console.error("❌ Failed to global search:", error);
    throw error;
  }
}

export type RegulationDocumentPresignedUrl = {
  presigned_url: string;
  filename?: string;
};

/**
 * POST /regulations/documents/user-presigned-url
 * Same contract as regulation-detail document preview / download.
 */
export async function getRegulationDocumentPresignedUrl(
  filePath: string,
): Promise<RegulationDocumentPresignedUrl> {
  const response = await apiClient.post<RegulationDocumentPresignedUrl>(
    "/regulations/documents/user-presigned-url",
    { file_path: filePath },
  );

  if (response.status === "success" && response.data?.presigned_url) {
    return response.data;
  }

  throw new Error(response.message || "Failed to get presigned URL");
}

/**
 * Get regulation details by ID
 */
export async function getRegulationById(
  id: string,
): Promise<ApiResponse<RegulationItem>> {
  try {
    const result = await apiClient.get<RegulationItem>(`/regulations/${id}`);
    return {
      status: (result.status as "success" | "error") || "success",
      code: result.code,
      message: result.message || "Success",
      data: result.data!,
    };
  } catch (error) {
    console.error("Failed to get regulation details:", error);
    throw error;
  }
}

/** Request body for POST /regulations/favorites (add one or more favorites) */
export interface RegulationsAddFavoritesBody {
  regulation_ids: string[];
}

/**
 * Mark regulations as favorites (POST /regulations/favorites)
 */
export async function addRegulationFavorites(
  regulation_ids: string[],
): Promise<ApiResponse<unknown>> {
  const body: RegulationsAddFavoritesBody = { regulation_ids };
  try {
    const result = await apiClient.post<unknown>(
      "/regulations/favorites",
      body,
    );
    return {
      status: (result.status as "success" | "error") || "success",
      code: result.code,
      message: result.message || "Success",
      data: result.data,
    };
  } catch (error) {
    console.error("Failed to add regulation favorites:", error);
    throw error;
  }
}

/**
 * Mark a single regulation as favorite (POST /regulations/favorites)
 */
export async function addRegulationFavorite(
  regulationId: string,
): Promise<ApiResponse<unknown>> {
  return addRegulationFavorites([regulationId]);
}

/**
 * Remove favorite (DELETE /regulations/{id}/favorite)
 */
export async function removeRegulationFavorite(
  regulationId: string,
): Promise<ApiResponse<unknown>> {
  const path = `/regulations/${encodeURIComponent(regulationId)}/favorite`;
  try {
    const result = await apiClient.delete<unknown>(path);
    return {
      status: (result.status as "success" | "error") || "success",
      code: result.code,
      message: result.message || "Success",
      data: result.data,
    };
  } catch (error) {
    console.error("Failed to remove regulation favorite:", error);
    throw error;
  }
}

/**
 * Remove favorites for multiple regulations (DELETE /regulations/{id}/favorite per id).
 */
export async function removeRegulationFavorites(
  regulation_ids: string[],
): Promise<void> {
  await Promise.all(regulation_ids.map((id) => removeRegulationFavorite(id)));
}

/** Request body for POST /regulations/export/xlsx */
export interface RegulationsExportXlsxBody {
  regulation_ids: string[];
}

/** Response payload (often nested under ApiResponse.data) */
export interface RegulationsExportXlsxPayload {
  file_path: string;
  presigned_url: string;
  expires_in?: number;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/** .xlsx (OOXML) is a ZIP container; local file header starts with "PK". */
function hasZipLocalHeader(bytes: Uint8Array): boolean {
  return bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b;
}

/** Read first bytes (jsdom Blob.slice may lack arrayBuffer()). */
async function readBlobPrefix(
  blob: Blob,
  byteLength: number,
): Promise<Uint8Array> {
  const slice = blob.slice(0, byteLength);
  if (typeof slice.arrayBuffer === "function") {
    const ab = await slice.arrayBuffer();
    return new Uint8Array(ab);
  }
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      if (fr.result instanceof ArrayBuffer) {
        resolve(new Uint8Array(fr.result));
      } else {
        reject(new Error("Export failed: cannot read response."));
      }
    };
    fr.onerror = () =>
      reject(fr.error ?? new Error("Export failed: cannot read response."));
    fr.readAsArrayBuffer(slice);
  });
}

async function readBlobAsText(blob: Blob): Promise<string> {
  if (typeof blob.text === "function") {
    return blob.text();
  }
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      resolve(typeof fr.result === "string" ? fr.result : "");
    };
    fr.onerror = () =>
      reject(fr.error ?? new Error("Export failed: cannot read response."));
    fr.readAsText(blob);
  });
}

function messageFromJsonExportError(body: Record<string, unknown>): string {
  const msg = body.message;
  if (typeof msg === "string" && msg.trim()) return msg;

  const detail = body.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) =>
        d &&
        typeof d === "object" &&
        "msg" in d &&
        typeof (d as { msg: unknown }).msg === "string"
          ? (d as { msg: string }).msg
          : null,
      )
      .filter((s): s is string => Boolean(s));
    if (parts.length) return parts.join("\n");
  }

  return "Export failed: server returned an error instead of a file.";
}

function extractExportXlsxPayload(
  result: unknown,
): RegulationsExportXlsxPayload {
  if (!result || typeof result !== "object") {
    throw new Error("Export failed: invalid response.");
  }
  const root = result as Record<string, unknown>;
  let node: Record<string, unknown> = root;
  if ("data" in root && root.data && typeof root.data === "object") {
    node = root.data as Record<string, unknown>;
  }
  const url = node.presigned_url;
  if (typeof url !== "string" || !url.trim()) {
    throw new Error(messageFromJsonExportError(root));
  }
  return {
    file_path: typeof node.file_path === "string" ? node.file_path : "",
    presigned_url: url.trim(),
    expires_in:
      typeof node.expires_in === "number" ? node.expires_in : undefined,
  };
}

/** Derive a filename hint from S3-style path (last segment). */
function filenameFromExportPath(filePath: string): string | null {
  if (!filePath?.trim()) return null;
  const seg = filePath
    .split("/")
    .filter(Boolean)
    .pop();
  if (!seg) return null;
  try {
    return decodeURIComponent(seg);
  } catch {
    return seg;
  }
}

function parseFilenameFromContentDisposition(header: string): string | null {
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];
  const utf8 = /filename\*=UTF-8''([^;\s]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return utf8[1];
    }
  }
  const plain = /filename=([^;\s]+)/i.exec(header);
  if (plain?.[1]) return plain[1].replace(/(?:^"|"$)/g, "");
  return null;
}

async function downloadExcelFromPresignedUrl(
  presignedUrl: string,
  fallbackFilename: string,
): Promise<void> {
  const res = await fetch(presignedUrl, {
    method: "GET",
    credentials: "omit",
    mode: "cors",
  });
  if (!res.ok) {
    let detail = "";
    try {
      detail = await res.text();
    } catch {
      /* ignore */
    }
    throw new Error(
      `Export download failed (${res.status}). ${detail.slice(0, 300)}`,
    );
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition");
  const nameFromHeader = cd ? parseFilenameFromContentDisposition(cd) : null;
  const filename = nameFromHeader || fallbackFilename;
  const validated = await validateExportIsXlsxBlob(blob);
  triggerBlobDownload(validated, filename);
}

/**
 * Detect JSON/HTML masquerading as xlsx (HTTP 200 with wrong body).
 * Avoids saving JSON text to .xlsx which Excel rejects as invalid format.
 */
async function validateExportIsXlsxBlob(blob: unknown): Promise<Blob> {
  if (!(blob instanceof Blob)) {
    throw new Error("Export failed: unexpected response type.");
  }
  if (blob.size === 0) {
    throw new Error("Export failed: empty file received.");
  }

  const head = await readBlobPrefix(blob, 4);
  if (hasZipLocalHeader(head)) {
    return blob;
  }

  const mime = (blob.type || "").toLowerCase();
  if (mime.includes("json")) {
    const text = await readBlobAsText(blob);
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(text) as Record<string, unknown>;
    } catch {
      throw new Error(
        "Export failed: server returned invalid JSON instead of an Excel file.",
      );
    }
    throw new Error(messageFromJsonExportError(parsed));
  }

  const text = await readBlobAsText(blob);
  const trimmed = text.trimStart();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(text) as Record<string, unknown>;
    } catch {
      throw new Error(
        "Export failed: server returned invalid JSON instead of an Excel file.",
      );
    }
    throw new Error(messageFromJsonExportError(parsed));
  }
  if (trimmed.startsWith("<")) {
    throw new Error(
      "Export failed: server returned HTML instead of an Excel file.",
    );
  }

  throw new Error("Export failed: response is not a valid Excel file.");
}

/**
 * Export selected regulations as XLSX (POST /regulations/export/xlsx).
 * API returns JSON with presigned_url; file is downloaded via GET to that URL.
 */
export async function exportRegulationsToXlsx(
  regulationIds: string[],
): Promise<void> {
  if (regulationIds.length === 0) {
    throw new Error("Select at least one regulation to export.");
  }
  const body: RegulationsExportXlsxBody = { regulation_ids: regulationIds };
  const result = await apiClient.post<unknown>(
    "/regulations/export/xlsx",
    body,
  );
  const payload = extractExportXlsxPayload(result);
  const dateStr = new Date().toISOString().slice(0, 10);
  const fallbackName =
    filenameFromExportPath(payload.file_path) ??
    `regulations-export-${dateStr}.xlsx`;
  await downloadExcelFromPresignedUrl(payload.presigned_url, fallbackName);
}

/**
 * Export API service
 */
export default {
  searchRegulations,
  getRegulationById,
  exportRegulationsToXlsx,
};

/**
 * Global filter search API response item
 */
export interface ConceptGlobalFilterItem {
  code: string;
  name: string;
  description?: string;
  /** Dimension/concept type when API returns it (e.g. when concept_name not passed) */
  dimension?: string;
  /** Parent concept code from values/search `concept.code` */
  concept_code?: string;
  /** values/search `concept.code` */
  concept_api_code?: string;
  /** values/search `concept.name` */
  concept_api_name?: string;
}

export type ValuesSearchConceptMeta = { code: string; name: string };

/** values/search API response: concept + values structure */
interface ConceptsValuesSearchItem {
  concept: { code: string; name: string; description?: string };
  values: Array<{ code: string; name: string; description?: string | null }>;
}

/**
 * Fetch concept name -> concept_code mapping from values/search API.
 * Used when selected concepts lack concept_code (e.g. from DEFAULT_CONCEPTS_LIST).
 * Returns Map<concept_name, concept_code> e.g. ProductType -> GCC-C000060.
 */
export async function getConceptCodeMapping(): Promise<Record<string, string>> {
  try {
    const result = await apiClient.get<ConceptsValuesSearchItem[]>(
      "/knowledge_manager/concepts/values/user-search",
      { page_size: 10, page: 1 },
    );
    const rawData = result.data ?? [];
    const mapping: Record<string, string> = {};
    if (Array.isArray(rawData)) {
      for (const item of rawData) {
        const name = item.concept?.name;
        const code = item.concept?.code;
        if (name && code) mapping[name] = code;
      }
    }
    return mapping;
  } catch (error) {
    console.error("Failed to fetch concept code mapping:", error);
    return {};
  }
}

/** Map API concept.name to display dimension */
export const CONCEPT_NAME_TO_DIMENSION: Record<string, string> = {
  ProductType: "Product Type",
  IngredientName: "Ingredient",
  TherapeuticArea: "Therapeutic Area",
  Department: "Department",
};

export function resolveConceptNamesForSearch(
  conceptCode: string,
  conceptName?: string,
): string[] {
  // API expects a single canonical concept key (e.g. TherapeuticArea), not
  // "TherapeuticArea,Therapeutic Area" which the backend treats as one bad name.
  for (const value of [conceptCode, conceptName]) {
    const trimmed = value?.trim();
    if (!trimmed) continue;

    for (const [apiName, dimension] of Object.entries(CONCEPT_NAME_TO_DIMENSION)) {
      if (
        dimension.toLowerCase() === trimmed.toLowerCase() ||
        apiName.toLowerCase() === trimmed.toLowerCase()
      ) {
        return [apiName];
      }
    }
  }

  const primary = conceptCode.trim() || conceptName?.trim() || "";
  return primary ? [primary] : [];
}

function mapConceptValuesSearchItems(
  rawData: ConceptsValuesSearchItem[],
): ConceptGlobalFilterItem[] {
  const items: ConceptGlobalFilterItem[] = [];

  for (const item of rawData) {
    const conceptName = item.concept?.name ?? "";
    const dimension = CONCEPT_NAME_TO_DIMENSION[conceptName] ?? conceptName;
    const conceptApiCode = item.concept?.code;
    const conceptApiName = item.concept?.name;
    for (const v of item.values ?? []) {
      if (!v?.code) continue;
      items.push({
        code: v.code,
        name: v.name ?? v.code,
        description: v.description ?? "",
        dimension,
        concept_code: conceptApiCode,
        concept_api_code: conceptApiCode,
        concept_api_name: conceptApiName,
      });
    }
  }

  return items;
}

/**
 * Load concept code/name pairs from values/search for resolving save payloads.
 * Keys include API concept.code, concept.name, and UI dimension label.
 */
export async function fetchValuesSearchConceptMetaByKey(): Promise<
  Map<string, ValuesSearchConceptMeta>
> {
  const map = new Map<string, ValuesSearchConceptMeta>();
  try {
    const result = await apiClient.get<ConceptsValuesSearchItem[]>(
      "/knowledge_manager/concepts/values/user-search",
      { page_size: 100, page: 1 },
    );
    const rawData = result.data ?? [];
    if (!Array.isArray(rawData)) return map;

    for (const item of rawData) {
      const code = item.concept?.code ?? "";
      const name = item.concept?.name ?? "";
      if (!code && !name) continue;
      const meta: ValuesSearchConceptMeta = { code, name };
      const dimension = CONCEPT_NAME_TO_DIMENSION[name] ?? name;
      for (const key of [code, name, dimension]) {
        if (key) map.set(key, meta);
      }
    }
  } catch (error) {
    console.error("Failed to fetch values/search concept metadata:", error);
  }
  return map;
}

/**
 * Get concepts via values/search API (for Impact Assessment checklist)
 * Transforms API response from { concept, values }[] to flat { code, name, dimension }[].
 * @param conceptNames - List of concept names to search, e.g. ['ProductType', 'Department']. Empty/undefined = all default concepts.
 * @param searchName - Search input value (from "Search concepts" field)
 * @param pageSize - Page size
 * @param page - 1-based page number for pagination (infinite scroll)
 */
export async function getConceptsValuesSearch(
  conceptNames: string[] | undefined,
  searchName: string | undefined,
  pageSize: number = 10,
  page: number = 1,
): Promise<ApiResponse<ConceptGlobalFilterItem[]>> {
  const params: Record<string, string | number | string[]> = {
    page_size: pageSize,
    page,
  };
  const searchNameTrimmed =
    typeof searchName === "string" ? searchName.trim() : undefined;
  if (searchNameTrimmed) params.name = searchNameTrimmed;

  if (conceptNames && conceptNames.length > 0) {
    params.concept_names = conceptNames;
  }

  try {
    // apiClient returns backend ApiResponse<T> directly (via interceptor: response.data)
    // Backend shape: { status, code, message, data: ConceptsValuesSearchItem[] }
    const result = await apiClient.get<ConceptsValuesSearchItem[]>(
      "/knowledge_manager/concepts/values/user-search",
      params,
    );

    const rawData = extractUserSearchResponseData(result.data ?? result);

    return {
      status: (result.status as "success" | "error") || "success",
      code: (result.code as number) ?? 200,
      message: (result.message as string) || "Success",
      data: mapConceptValuesSearchItems(rawData),
    };
  } catch (error) {
    console.error("Failed to get concepts from values/search:", error);
    return {
      status: "error",
      code: 500,
      message: error instanceof Error ? error.message : "Unknown error",
      data: [],
    };
  }
}

/**
 * Options for paginated concept values (infinite scroll)
 */
export interface GetConceptValuesOptions {
  page?: number;
  page_size?: number;
  /** When set to `code`, the search term is sent as `code` instead of `name`. */
  searchBy?: "name" | "code";
}

/**
 * Get concept values list (for dropdown options).
 * Supports optional pagination for infinite scroll (page, page_size).
 * @param conceptName - Concept name, e.g. 'ProductType', 'country'
 * @param searchName - Optional search keyword
 * @param options - Optional { page, page_size } for paginated load
 */
export async function getConceptValues(
  conceptName: string,
  searchName?: string,
  options?: GetConceptValuesOptions,
): Promise<ApiResponse<ConceptValue[]>> {
  const params: Record<string, any> = {
    concept_name: conceptName,
  };

  if (searchName) {
    if (options?.searchBy === "code") {
      params.code = searchName;
    } else {
      params.name = searchName;
    }
  }
  if (options?.page != null) {
    params.page = options.page;
  }
  if (options?.page_size != null) {
    params.page_size = options.page_size;
  }

  try {
    const result = await apiClient.get<
      | ConceptValue[]
      | {
          items?: ConceptValue[];
          values?: ConceptValue[];
          data?: ConceptValue[];
          total?: number;
        }
    >("/knowledge_manager/concepts/values", params);
    // Normalize: API may return array directly, or wrapped in { items } / { values } / { data }
    let data: ConceptValue[] = [];
    if (Array.isArray(result.data)) {
      data = result.data;
    } else if (result.data && typeof result.data === "object") {
      const wrapped = result.data as {
        items?: ConceptValue[];
        values?: ConceptValue[];
        data?: ConceptValue[];
        total?: number;
      };
      if (Array.isArray(wrapped.data)) {
        data = wrapped.data;
      } else if (Array.isArray(wrapped.items)) {
        data = wrapped.items;
      } else if (Array.isArray(wrapped.values)) {
        data = wrapped.values;
      }
    }
    return {
      status: (result.status as "success" | "error") || "success",
      code: result.code,
      message: result.message || "Success",
      data,
    };
  } catch (error) {
    console.error(`Failed to get concept values for ${conceptName}:`, error);
    return {
      status: "error",
      code: 500,
      message: error instanceof Error ? error.message : "Unknown error",
      data: [],
    };
  }
}

function extractUserSearchResponseData(
  payload: unknown,
): ConceptsValuesSearchItem[] {
  if (Array.isArray(payload)) {
    return payload as ConceptsValuesSearchItem[];
  }
  if (!payload || typeof payload !== "object") return [];

  const record = payload as Record<string, unknown>;
  if (Array.isArray(record.data)) {
    return record.data as ConceptsValuesSearchItem[];
  }
  if (Array.isArray(record.items)) {
    return record.items as ConceptsValuesSearchItem[];
  }
  if ("concept" in record && "values" in record) {
    return [payload as ConceptsValuesSearchItem];
  }
  return [];
}

function flattenConceptValuesFromUserSearch(
  rawData: ConceptsValuesSearchItem[] | null | undefined,
  conceptFilters: string[] = [],
): ConceptValue[] {
  if (!Array.isArray(rawData)) return [];

  const normalizedFilters = [
    ...new Set(
      conceptFilters.map((value) => value.trim().toLowerCase()).filter(Boolean),
    ),
  ];
  const values: ConceptValue[] = [];

  for (const item of rawData) {
    const conceptName = item.concept?.name?.toLowerCase() ?? "";
    const conceptCode = item.concept?.code?.toLowerCase() ?? "";
    if (
      normalizedFilters.length > 0 &&
      !normalizedFilters.some(
        (filter) => filter === conceptName || filter === conceptCode,
      )
    ) {
      continue;
    }

    for (const value of item.values ?? []) {
      if (!value?.code) continue;
      values.push({
        id: value.code,
        code: value.code,
        name: value.name ?? value.code,
        description: value.description ?? "",
      });
    }
  }

  return values;
}

export function isConceptValuesUserSearchSuccess(
  response: ApiResponse<ConceptValue[]>,
): boolean {
  return (
    response.status === "success" ||
    response.code === 200 ||
    response.code === 201
  );
}

/**
 * Get concept values via user-search API (for AI plan concept value picker).
 * Supports pagination and optional keyword search.
 */
export async function getConceptValuesUserSearch(
  conceptName: string,
  searchName?: string,
  options?: GetConceptValuesOptions & { conceptCode?: string },
): Promise<ApiResponse<ConceptValue[]>> {
  const conceptKey = (options?.conceptCode ?? conceptName).trim();
  if (!conceptKey) {
    return {
      status: "error",
      code: 400,
      message: "Concept is required",
      data: [],
    };
  }

  const result = await getConceptsValuesSearch(
    [conceptKey],
    searchName,
    options?.page_size ?? 20,
    options?.page ?? 1,
  );

  const data = (result.data ?? []).map((item) => ({
    id: item.code,
    code: item.code,
    name: item.name ?? item.code,
    description: item.description ?? "",
  }));

  return {
    status: result.status,
    code: result.code,
    message: result.message,
    data,
  };
}

/**
 * Get regulations fields configuration
 * @param pageType - Page type, e.g., 'list'
 */
export async function getRegulationsFields(
  pageType: string = "list",
): Promise<ApiResponse<RegulationsFieldsData>> {
  try {
    const result = await apiClient.get<RegulationsFieldsData>(
      "/regulations/fields",
      { page_type: pageType },
    );

    // Debug logs (also asserted in unit tests)
    console.log("Regulations Fields Response:", result);
    console.log("Fields Data:", result.data);

    const rawFields = result.data?.fields;
    const fields = Array.isArray(rawFields)
      ? rawFields.map((field) =>
          normalizeRegulationsFieldConfig(
            field as unknown as Record<string, unknown>,
          ),
        )
      : [];

    return {
      status: (result.status as "success" | "error") || "success",
      code: result.code,
      message: result.message || "Success",
      data: {
        fields,
        page_type: result.data?.page_type ?? pageType,
      },
    };
  } catch (error) {
    console.error("Failed to get regulations fields:", error);
    return {
      status: "error",
      code: 500,
      message: error instanceof Error ? error.message : "Unknown error",
      data: { fields: [], page_type: pageType },
    };
  }
}

/**
 * User filter criteria structure
 */
export interface UserFilterCriteria {
  version?: string;
  markets?: string[];
  product_types?: string[];
  ha?: string[];
  tags?: string[];
  start_date?: string;
  end_date?: string;
  keyword?: string;
}

/**
 * User filter data structure
 */
export interface UserFilter {
  id: number;
  name: string;
  criteria: UserFilterCriteria;
  is_favorite: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/**
 * User filters response data structure
 */
export interface UserFiltersData {
  filters: UserFilter[];
  total: number;
}

/**
 * Get user saved filters
 */
export async function getUserFilters(): Promise<ApiResponse<UserFilter[]>> {
  try {
    const result = await apiClient.get<UserFiltersData>("/user-filters");

    // Extract filters array from the response data
    const filters = result.data?.filters || [];

    return {
      status: (result.status as "success" | "error") || "success",
      code: result.code,
      message: result.message || "Success",
      data: filters,
    };
  } catch (error) {
    console.error("Failed to get user filters:", error);
    return {
      status: "error",
      code: 500,
      message: error instanceof Error ? error.message : "Unknown error",
      data: [],
    };
  }
}

/**
 * Create user filter request parameters
 */
export interface CreateUserFilterRequest {
  name: string;
  criteria: {
    markets?: string[];
    product_types?: string[];
    ha?: string[];
    tags?: string[];
    start_date?: string;
    end_date?: string;
    keyword?: string;
  };
  is_favorite?: boolean;
}

/**
 * Create a new user filter
 */
export async function createUserFilter(
  request: CreateUserFilterRequest,
): Promise<ApiResponse<UserFilter[]>> {
  try {
    const result = await apiClient.post<UserFiltersData>(
      "/user-filters",
      request,
    );

    // Extract filters array from the response data
    const filters = result.data?.filters || [];

    return {
      status: (result.status as "success" | "error") || "success",
      code: result.code,
      message: result.message || "Filter created successfully",
      data: filters,
    };
  } catch (error) {
    console.error("Failed to create user filter:", error);
    throw error;
  }
}

/**
 * Update an existing user filter
 */
export async function updateUserFilter(
  filterId: number,
  request: CreateUserFilterRequest,
): Promise<ApiResponse<UserFilter[]>> {
  try {
    const result = await apiClient.put<UserFiltersData>(
      `/user-filters/${filterId}`,
      request,
    );

    // Extract filters array from the response data
    const filters = result.data?.filters || [];

    return {
      status: (result.status as "success" | "error") || "success",
      code: result.code,
      message: result.message || "Filter updated successfully",
      data: filters,
    };
  } catch (error) {
    console.error("Failed to update user filter:", error);
    throw error;
  }
}

/**
 * Delete a user filter
 */
export async function deleteUserFilter(
  filterId: number,
): Promise<ApiResponse<null>> {
  try {
    const result = await apiClient.delete<null>(`/user-filters/${filterId}`);

    return {
      status: (result.status as "success" | "error") || "success",
      code: result.code,
      message: result.message || "Filter deleted successfully",
      data: null,
    };
  } catch (error) {
    console.error("Failed to delete user filter:", error);
    throw error;
  }
}

/**
 * Reorder user filters by id list
 */
export async function reorderUserFilters(
  filterIds: number[],
): Promise<ApiResponse<null>> {
  try {
    const result = await apiClient.put<null>("/user-filters/reorder", {
      filter_ids: filterIds,
    });

    return {
      status: (result.status as "success" | "error") || "success",
      code: result.code,
      message: result.message || "Reordered filters successfully",
      data: null,
    };
  } catch (error) {
    console.error("Failed to reorder user filters:", error);
    throw error;
  }
}

/**
 * Workflow update request (for Associate/Initiate Process)
 * POST /manual-assessment/workflow
 */
export interface UpdateWorkflowRequest {
  regulationId: string;
  regulationVersion: string;
  workflowId: string;
  processInstanceId: string | null;
  processDefinitionId: string;
  processAssociationId: string;
  processStatus: string;
}

/**
 * Workflow update response
 */
export interface UpdateWorkflowResponse {
  id: number;
  uuid: string;
  creator: string;
  createdAt: string;
  processInstanceId: string;
  processName: string | null;
  processDefinitionId: string;
  processAssociationId: string;
  status: string;
  creatorName: string;
}

/**
 * Update workflow after Associate/Initiate Process (Plan & Track integration)
 */
export async function updateWorkflow(
  request: UpdateWorkflowRequest,
): Promise<ApiResponse<UpdateWorkflowResponse>> {
  const result = await apiClient.post<UpdateWorkflowResponse>(
    "/manual-assessment/workflow",
    request,
  );
  return {
    status: (result.status as "success" | "error") || "success",
    code: result.code,
    message: result.message || "Workflow updated successfully",
    data: result.data ?? null,
  };
}

/**
 * Regulation workflow item (from list endpoint)
 */
export interface RegulationWorkflowItem {
  uuid?: string;
  workflow_id?: string;
  id?: string;
  [key: string]: unknown;
}

/**
 * Workflow task item (legacy / generic)
 */
export interface WorkflowTaskItem {
  name?: string;
  taskName?: string;
  status?: string;
  [key: string]: unknown;
}

/**
 * Process task instance from workflow API (processTaskInstances item)
 */
export interface ProcessTaskInstance {
  taskInstanceName?: string;
  runningStatus?: "Completed" | "In Progress" | "Not Started" | "Unassigned";
  taskId?: string;
  taskDefinitionId?: string;
  taskInstanceUniqueId?: string;
  processAssociationId?: string;
  [key: string]: unknown;
}

/**
 * Task property value item (propertyValueItemList item from Get Workflow Task)
 */
export interface PropertyValueItem {
  field?: string;
  label?: string;
  originlabel?: string;
  type?: string;
  required?: boolean;
  hide?: boolean;
  order?: number;
  /** When true, field is PAT-managed. Still include it in customPropertyMap on save/submit. */
  systemDefault?: boolean;
  fieldValue?: unknown;
  options?: Array<{ label: string; value: string | number }>;
  asyncConfig?: {
    url?: string;
    basicUrl?: string;
    labelPath?: string;
    valuePath?: string;
    listDataPath?: string;
    params?: Array<{ value?: string; fieldName?: string }>;
  };
  [key: string]: unknown;
}

/**
 * Person on workflow task (reviewer, assignee, etc.)
 */
export interface WorkflowTaskPerson {
  id?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

/** Attachment on workflow task (e.g. AI Impact Assessment Report PDF). */
export interface WorkflowTaskReference {
  uri?: string;
  name?: string;
  label?: string;
  content_type?: string;
  field?: string;
  value?: string;
  processAssociationId?: string;
  source?: string;
  order?: number;
}

/** One prior-step group inside `sourceFields.items`. */
export interface WorkflowSourceFieldGroup {
  sourceTaskName?: string;
  taskInstanceUniqueId?: string;
  fields?: PropertyValueItem[];
}

/**
 * Selected prior-step fields (readonly context) when `previousTask` is null
 * or only a subset of the prior step should appear.
 */
export interface WorkflowSourceFields {
  enabled?: boolean;
  items?: WorkflowSourceFieldGroup[];
}

/** Prior completed task snapshot when `include_previous_task=true`. */
export interface WorkflowPreviousTask {
  propertyValueItemList?: PropertyValueItem[];
  taskId?: string;
  taskInstanceName?: string;
  taskInstanceUniqueId?: string;
  runningStatus?: string;
  statusAction?: string;
  actualStartDate?: string;
  actualEndDate?: string;
  reviewers?: WorkflowTaskPerson[];
  taskAssignee?: WorkflowTaskPerson;
  references?: WorkflowTaskReference[];
  [key: string]: unknown;
}

/**
 * Workflow task detail (from GET /manual-assessment/workflows/{id}/tasks/{taskId})
 */
export interface WorkflowTaskDetail {
  task?: {
    taskId?: string;
    taskDefinitionId?: string;
    taskInstanceName?: string;
    taskInstanceUniqueId?: string;
    processAssociationId?: string;
    runningStatus?: string;
    performedBy?: string;
    reviewers?: WorkflowTaskPerson[];
    taskAssignee?: WorkflowTaskPerson;
    dueDate?: string;
    actualStartDate?: string;
    propertyValueItemList?: PropertyValueItem[];
    references?: WorkflowTaskReference[];
    [key: string]: unknown;
  };
  associatedEntity?: { type?: string; details?: unknown };
  /**
   * Prior completed task snapshot. Live API may nest under `taskPage.previousTask`;
   * `parseWorkflowTaskDetailFromApiResponse` lifts it to this root field.
   */
  previousTask?: WorkflowPreviousTask | null;
  /**
   * `inline` | `modal`. Live API may nest under `taskPage.previousTaskDisplayMode`;
   * normalize lifts it to root. Default UI when missing: inline form / modal link when previousTask has fields.
   */
  previousTaskDisplayMode?: "inline" | "modal" | string | null;
  /**
   * Prior-step field snippets. Live API may nest under `task` or `taskPage`;
   * normalize lifts it to this root field.
   */
  sourceFields?: WorkflowSourceFields | null;
  /** Task form page config (headers, etc.) from GET workflow task. */
  taskPage?: {
    headers?: PropertyValueItem[];
    taskTables?: import("../types/manualAssessmentWorkflow").TaskTableDefinition[];
    assignNextTask?: import("../utils/assignNextTaskFormConfig").AssignNextTaskDefinition;
    [key: string]: unknown;
  };
  taskTables?: import("../types/manualAssessmentWorkflow").TaskTableDefinition[];
  /**
   * E-signature gate for this task submit. Prefer this over the hidden
   * `esignIntegrationMarker.esignIntegration` property item.
   */
  eSignPolicy?: import("../types/eSignPolicy").ESignPolicy | null;
  /** Workflow-level references on GET task (sibling to `task`, e.g. originalTitle). */
  references?: WorkflowTaskReference[];
  [key: string]: unknown;
}

/**
 * Workflow detail (from GET /manual-assessment/workflows/{id})
 */
export interface WorkflowDetail {
  uuid?: string;
  workflow_id?: string;
  processName?: string;
  process_name?: string;
  processAssociationId?: string;
  process_association_id?: string;
  status?: string;
  progress?: number;
  processTaskInstances?: ProcessTaskInstance[];
  tasks?: WorkflowTaskItem[];
  recentlyCompleted?: WorkflowTaskItem[];
  currentTasks?: WorkflowTaskItem[];
  upcomingTasks?: WorkflowTaskItem[];
  [key: string]: unknown;
}

/**
 * Get assessment filter options for a regulation
 * GET /regulations/{id}/{version}/assessments/filter-options
 */
export async function getAssessmentFilterOptions(
  regulationId: string,
  version: string,
): Promise<
  ApiResponse<
    Array<{ name: string; options: Array<{ name: string; value: string }> }>
  >
> {
  const normalizedVersion = version?.startsWith("v")
    ? version.slice(1)
    : version;
  const result = await apiClient.get<
    Array<{ name: string; options: Array<{ name: string; value: string }> }>
  >(
    `/regulations/${regulationId}/${normalizedVersion}/assessments/filter-options`,
  );
  return {
    status: (result.status as "success" | "error") || "success",
    code: result.code,
    message: result.message ?? null,
    data: result.data ?? [],
  };
}

/**
 * Get category overview (summary) filter options for a regulation
 * GET /regulations/{id}/{version}/assessments/overview/filter-options
 */
export async function getCategoryOverviewFilterOptions(
  regulationId: string,
  version: string,
): Promise<
  ApiResponse<
    Array<{ name: string; options: Array<{ name: string; value: string }> }>
  >
> {
  const normalizedVersion = version?.startsWith("v")
    ? version.slice(1)
    : version;
  const result = await apiClient.get<
    Array<{ name: string; options: Array<{ name: string; value: string }> }>
  >(
    `/regulations/${regulationId}/${normalizedVersion}/assessments/overview/filter-options`,
  );
  return {
    status: (result.status as "success" | "error") || "success",
    code: result.code,
    message: result.message ?? null,
    data: result.data ?? [],
  };
}

/**
 * List workflows for a regulation
 * GET /manual-assessment/regulations/{id}/{version}/workflows
 */
export async function listRegulationWorkflows(
  regulationId: string,
  version: string,
): Promise<ApiResponse<RegulationWorkflowItem[]>> {
  const normalizedVersion = version?.startsWith("v")
    ? version.slice(1)
    : version;
  const result = await apiClient.get<RegulationWorkflowItem[]>(
    `/manual-assessment/regulations/${regulationId}/${normalizedVersion}/workflows`,
  );
  const payload = (result as any)?.data?.data ?? (result as any)?.data ?? {};
  const items = Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload)
    ? payload
    : [];
  return {
    status: (result.status as "success" | "error") || "success",
    code: result.code,
    message: result.message ?? null,
    data: items,
  };
}

/**
 * Get workflow detail by ID
 * GET /manual-assessment/workflows/{workflow_id}
 */
export async function getWorkflow(
  workflowId: string,
): Promise<ApiResponse<WorkflowDetail>> {
  const result = await apiClient.get<WorkflowDetail>(
    `/manual-assessment/workflows/${workflowId}`,
  );
  return {
    status: (result.status as "success" | "error") || "success",
    code: result.code,
    message: result.message ?? null,
    data: result.data ?? null,
  };
}

/**
 * Get workflow task detail (form schema + values)
 * GET /manual-assessment/workflows/{workflow_id}/tasks/{task_id}?include_previous_task=true
 */
export async function getWorkflowTask(
  workflowId: string,
  taskId: string,
): Promise<ApiResponse<WorkflowTaskDetail>> {
  const result = await apiClient.get<WorkflowTaskDetail>(
    `/manual-assessment/workflows/${workflowId}/tasks/${taskId}`,
    { include_previous_task: true },
  );
  const detail = parseWorkflowTaskDetailFromApiResponse(result);
  return {
    status: (result.status as "success" | "error") || "success",
    code: result.code,
    message: result.message ?? null,
    data: detail,
  };
}

/**
 * Save task (draft) payload
 * POST /manual-assessment/workflows/{workflow_id}/tasks/{task_id}/save
 */
export interface TaskTableFieldsValueEntry {
  taskInstanceUniqueId?: string;
  taskDefinitionId?: string;
  /** Row index (e.g. department) for Overall Review virtual decision rows. */
  department?: string;
  customPropertyMap: Record<string, unknown>;
}

export interface TaskTableSavePayload {
  name: string;
  tasks?: unknown[];
  fieldsValueMap: TaskTableFieldsValueEntry[];
}

export interface SaveTaskPayload {
  processAssociationId: string;
  taskDefinitionId: string;
  taskInstanceUniqueId: string;
  /**
   * Top-level save outcome. Task tables use the same derivation as submit
   * (e.g. `["skip_all"]`, `["approve_all"]`, or department ids).
   * A dynamic-form Review / Reject (or other) `taskResult` select is sent here
   * when tables do not supply one. Never put this key in `customPropertyMap`.
   */
  taskResult?: string | string[];
  taskCustomAssignee?: {
    taskUsers?: string[];
    taskGroups?: string[];
    taskRoles?: string[];
  };
  milestone?: boolean;
  milestoneName?: string;
  milestoneDate?: string;
  customPropertyMap?: Record<string, unknown>;
  taskTables?: TaskTableSavePayload | TaskTableSavePayload[];
  assignNextTask?: Record<string, unknown>;
}

export async function saveTask(
  workflowId: string,
  taskId: string,
  payload: SaveTaskPayload,
): Promise<ApiResponse<boolean | unknown>> {
  const result = await apiClient.post<boolean | unknown>(
    `/manual-assessment/workflows/${encodeURIComponent(
      workflowId,
    )}/tasks/${encodeURIComponent(taskId)}/save`,
    payload,
  );
  return {
    status: (result.status as "success" | "error") || "success",
    code: result.code,
    message: result.message ?? null,
    data: result.data ?? null,
  };
}

/**
 * Submit Tasks API
 * Used for Approve / Reject and other task outcomes (e.g. triage, department assessment).
 *
 * Endpoint: POST /manual-assessment/workflows/{workflow_id}/tasks/{task_id}/submit
 *
 * Key inputs:
 * - taskId: task instance id
 * - taskDefinitionId: activity/definition id
 * - taskResult: outcome (from the task definition's taskResult field; e.g. "assessment_required",
 *   "impact_found_yes", "impact_found_no", or workflow-specific approved/rejected values)
 * - customPropertyMap: all property field/value pairs, including systemDefault: true
 *
 * Request body: taskId, taskDefinitionId, taskResult, customPropertyMap; when the task
 * detail includes taskTables, also taskCustomAssignee and taskTables.
 * Response: { status: "success", code: 200, message: null, data: true }
 */
export interface SubmitTaskPayload {
  taskId: string;
  taskDefinitionId: string;
  /**
   * Task outcome. Overall Approve (§4.5) sends a string array
   * e.g. `["approve_all"]` or `["Reject:DEPA000010","Reopen:DEPA000011"]`.
   * Triage / Identify SME with every assessment_required off sends `["skip_all"]`.
   * Other tasks typically send a single string.
   */
  taskResult: string | string[];
  customPropertyMap?: Record<string, unknown>;
  taskCustomAssignee?: SaveTaskPayload["taskCustomAssignee"];
  taskTables?: TaskTableSavePayload | TaskTableSavePayload[];
  assignNextTask?: Record<string, unknown>;
  /** E-sign proof from ESignValidation success (Login Authentication). */
  eSign?: {
    esignId: string;
  };
}

export async function submitTask(
  workflowId: string,
  taskId: string,
  payload: SubmitTaskPayload,
): Promise<ApiResponse<boolean | unknown>> {
  const result = await apiClient.post<boolean | unknown>(
    `/manual-assessment/workflows/${encodeURIComponent(
      workflowId,
    )}/tasks/${encodeURIComponent(taskId)}/submit`,
    payload,
  );
  return {
    status: (result.status as "success" | "error") || "success",
    code: result.code,
    message: result.message ?? null,
    data: result.data ?? null,
  };
}

export type ReassignTaskPayload = {
  newAssignee: {
    userId: string;
  };
};

/**
 * PATCH /manual-assessment/workflows/{workflowId}/tasks/{taskId}/assignee
 */
export async function reassignWorkflowTask(
  workflowId: string,
  taskId: string,
  payload: ReassignTaskPayload,
): Promise<ApiResponse<boolean | unknown>> {
  const result = await apiClient.patch<boolean | unknown>(
    `/manual-assessment/workflows/${encodeURIComponent(
      workflowId,
    )}/tasks/${encodeURIComponent(taskId)}/assignee`,
    payload,
  );
  return {
    status: (result.status as "success" | "error") || "success",
    code: result.code,
    message: result.message ?? null,
    data: result.data ?? null,
  };
}
